// Global Layer Properties — diagnostics builder.
//
// The single, pure entry point that fuses a viewer `Layer` + optional context
// (store visibility, live engine diagnostics, gTwin scene item, session
// override) into the {@link LayerDiagnostics} view model the 7-tab panel
// renders. Layer-KIND aware, renderer aware, contract aware, and safe with
// missing metadata — `buildLayerDiagnostics(layer)` with no context is valid.

import type { EngineId, Layer } from '@geosoft/viewer-sdk';
import { buildCoordinateDiagnostics } from './coordinates';
import { buildOutputFilesDiagnostics } from './outputFiles';
import { buildLayerPreview } from './preview';
import {
  coerceRenderPath,
  renderPathLabel,
  resolveExpectedRenderPath,
} from './renderPath';
import { buildSourceDiagnostics } from './source';
import { computeLayerWarnings } from './warnings';
import type {
  LayerDiagnostics,
  LayerDiagnosticsContext,
  LayerHealthLabel,
  LayerOverviewDiagnostics,
  LayerRenderPathDiagnostics,
  LiveRenderDiagnostics,
} from './types';

const HEALTH_VALUES: ReadonlySet<LayerHealthLabel> = new Set<LayerHealthLabel>([
  'ready',
  'loading',
  'failed',
  'unsupported',
  'deferred',
  'pending',
  'unknown',
]);

function coerceHealth(...values: Array<string | null | undefined>): LayerHealthLabel {
  for (const value of values) {
    if (typeof value === 'string' && HEALTH_VALUES.has(value as LayerHealthLabel)) {
      return value as LayerHealthLabel;
    }
  }
  return 'unknown';
}

function nullableBool(value: boolean | null | undefined): boolean | null {
  return typeof value === 'boolean' ? value : null;
}

function computeVisibilityInSync(
  storeVisible: boolean | null,
  mounted: boolean | null,
  engineVisible: boolean | null,
): boolean | null {
  if (storeVisible === null) return null;
  if (storeVisible && mounted === false) return false;
  if (engineVisible === null) return null;
  return storeVisible === engineVisible;
}

function buildRenderPathDiagnostics(
  layer: Layer,
  ctx: LayerDiagnosticsContext,
  live: LiveRenderDiagnostics | null,
): LayerRenderPathDiagnostics {
  const expected = resolveExpectedRenderPath(layer, {
    primaryEngine: ctx.primaryEngine,
    availableEngines: ctx.availableEngines,
  });
  const livePath = coerceRenderPath(live?.renderPath);
  const path = livePath ?? expected.path;
  const adapterId: EngineId | null = live?.adapterId ?? expected.adapter;
  const mounted = nullableBool(live?.mounted);
  const storeVisible = nullableBool(ctx.storeVisible);
  const engineVisible = nullableBool(live?.engineVisible);

  return {
    mounted,
    adapterId,
    renderPath: path,
    renderPathLabel: renderPathLabel(path),
    expectedRenderPath: expected.path,
    expectedAdapter: expected.adapter,
    engineLayerIds: live?.engineLayerIds ? [...live.engineLayerIds] : [],
    engineObjectIds: live?.engineObjectIds ? [...live.engineObjectIds] : [],
    storeVisible,
    engineVisible,
    visibilityInSync: computeVisibilityInSync(storeVisible, mounted, engineVisible),
    lastError: typeof live?.lastError === 'string' ? live.lastError : null,
    decodedTileCount: typeof live?.decodedTileCount === 'number' ? live.decodedTileCount : null,
    renderedFeatureCount: typeof live?.renderedFeatureCount === 'number' ? live.renderedFeatureCount : null,
    lastTileError: typeof live?.lastTileError === 'string' ? live.lastTileError : null,
    renderFrameAssumptions: Array.isArray(live?.renderFrameAssumptions)
      ? [...live.renderFrameAssumptions]
      : [],
    live: live !== null,
  };
}

function buildOverview(
  layer: Layer,
  ctx: LayerDiagnosticsContext,
  live: LiveRenderDiagnostics | null,
  renderPath: LayerRenderPathDiagnostics,
): LayerOverviewDiagnostics {
  return {
    id: layer.id,
    label: typeof layer.label === 'string' && layer.label.length > 0 ? layer.label : null,
    kind: String(layer.kind ?? 'unknown'),
    slot: typeof ctx.slot === 'string' ? ctx.slot : null,
    sceneProfile: typeof layer.sceneProfile === 'string' ? layer.sceneProfile : null,
    activationTier: typeof layer.activationTier === 'string' ? layer.activationTier : null,
    visualPurpose: typeof layer.visualPurpose === 'string' ? layer.visualPurpose : null,
    storeVisible: nullableBool(ctx.storeVisible),
    engineVisible: nullableBool(live?.engineVisible),
    health: coerceHealth(ctx.health, live?.health),
    adapterOwner: renderPath.adapterId,
    renderPath: renderPath.renderPath,
    renderPathLabel: renderPath.renderPathLabel,
    mounted: renderPath.mounted,
  };
}

/**
 * Build the full {@link LayerDiagnostics} for `layer`. Pure: no React, no I/O.
 * Every section degrades gracefully when context is absent.
 */
export function buildLayerDiagnostics(
  layer: Layer,
  context: LayerDiagnosticsContext = {},
): LayerDiagnostics {
  const live = context.live ?? null;

  const coordinates = buildCoordinateDiagnostics(layer, {
    override: context.override,
    live,
    viewerFrameCrs: context.viewerFrameCrs,
    groupLabel: context.groupLabel,
    groupMembers: context.groupMembers,
  });
  const source = buildSourceDiagnostics(layer, live);
  const renderPath = buildRenderPathDiagnostics(layer, context, live);
  const overview = buildOverview(layer, context, live, renderPath);
  const outputFiles = buildOutputFilesDiagnostics(layer);
  const preview = buildLayerPreview(layer, source);
  const warnings = computeLayerWarnings({ layer, coordinates, source, renderPath, live });

  return {
    overview,
    source,
    coordinates,
    renderPath,
    outputFiles,
    raw: {
      layerJson: layer,
      sceneItemJson: context.sceneItemJson ?? null,
      engineHealthJson: context.engineHealthJson ?? null,
    },
    preview,
    warnings,
  };
}
